/**
 * Budget Target Setting
 * HQ sets annual/quarterly targets for the bank
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BudgetTargetSetting = () => {
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [targets, setTargets] = useState([]);
  const [newTarget, setNewTarget] = useState({
    fiscal_year: new Date().getFullYear(),
    period_type: 'annual',
    period_start: '',
    period_end: '',
    revenue_target: '',
    expense_target: '',
    profit_target: ''
  });
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrg) {
      fetchTargets();
    }
  }, [selectedOrg]);

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('token');
      // Get bank tenants first
      const bankResponse = await axios.get(`${API_URL}/api/tenants?tenant_type=bank`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (bankResponse.data.length > 0) {
        const bankId = bankResponse.data[0].id;
        // Get orgs for first bank
        const orgResponse = await axios.get(`${API_URL}/api/organizations?tenant_id=${bankId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrgs(orgResponse.data);
        if (orgResponse.data.length > 0) {
          setSelectedOrg(orgResponse.data[0].id);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchTargets = async () => {
    try {
      const token = localStorage.getItem('token');
      const year = new Date().getFullYear();
      const response = await axios.get(
        `${API_URL}/api/budget-targets/org/${selectedOrg}/${year}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTargets(response.data);
    } catch (error) {
      console.error('Error:', error);
      setTargets([]);
    }
  };

  const createTarget = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/budget-targets`, {
        organization_id: selectedOrg,
        ...newTarget
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowForm(false);
      fetchTargets();
      alert('Budget target created!');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="p-8">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Budget Target Setting</h1>
          <p className="text-gray-600 mt-1">Set financial targets for HQ and branches</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          + Set New Target
        </button>
      </div>

      {/* Organization Selector */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <label className="block text-sm font-medium mb-2">Select Organization</label>
        <select
          value={selectedOrg}
          onChange={(e) => setSelectedOrg(e.target.value)}
          className="w-full border rounded-lg px-4 py-2"
        >
          <option value="">Select organization...</option>
          {orgs.map(org => (
            <option key={org.id} value={org.id}>
              {org.hierarchy_path || org.name} ({org.type})
            </option>
          ))}
        </select>
      </div>

      {/* Targets List */}
      <div className="grid grid-cols-1 gap-4">
        {targets.map(target => (
          <div key={target.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold">FY {target.fiscal_year} - {target.period_type}</h3>
                <p className="text-sm text-gray-600">
                  {new Date(target.period_start).toLocaleDateString()} - {new Date(target.period_end).toLocaleDateString()}
                </p>
              </div>
              {target.cascaded_from && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  Cascaded {target.cascade_percentage}%
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Revenue Target</div>
                <div className="text-2xl font-bold text-green-600">
                  ETB {(target.revenue_target / 1000000).toFixed(1)}M
                </div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Expense Target</div>
                <div className="text-2xl font-bold text-orange-600">
                  ETB {(target.expense_target / 1000000).toFixed(1)}M
                </div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Profit Target</div>
                <div className="text-2xl font-bold text-blue-600">
                  ETB {(target.profit_target / 1000000).toFixed(1)}M
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {targets.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-lg">
          No targets set for this organization. Click "Set New Target" to begin.
        </div>
      )}

      {/* Create Target Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full">
            <h2 className="text-xl font-bold mb-4">Set Budget Target</h2>
            <form onSubmit={createTarget}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Fiscal Year</label>
                  <input
                    type="number"
                    value={newTarget.fiscal_year}
                    onChange={(e) => setNewTarget({...newTarget, fiscal_year: parseInt(e.target.value)})}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Period Type</label>
                  <select
                    value={newTarget.period_type}
                    onChange={(e) => setNewTarget({...newTarget, period_type: e.target.value})}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Start Date</label>
                  <input
                    type="date"
                    value={newTarget.period_start}
                    onChange={(e) => setNewTarget({...newTarget, period_start: e.target.value})}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">End Date</label>
                  <input
                    type="date"
                    value={newTarget.period_end}
                    onChange={(e) => setNewTarget({...newTarget, period_end: e.target.value})}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>
              </div>
              
              <div className="border-t pt-4 mb-4">
                <h3 className="font-bold mb-3">Financial Targets (ETB)</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Revenue</label>
                    <input
                      type="number"
                      value={newTarget.revenue_target}
                      onChange={(e) => setNewTarget({...newTarget, revenue_target: e.target.value})}
                      placeholder="e.g., 50000000"
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Expense</label>
                    <input
                      type="number"
                      value={newTarget.expense_target}
                      onChange={(e) => setNewTarget({...newTarget, expense_target: e.target.value})}
                      placeholder="e.g., 35000000"
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Profit</label>
                    <input
                      type="number"
                      value={newTarget.profit_target}
                      onChange={(e) => setNewTarget({...newTarget, profit_target: e.target.value})}
                      placeholder="e.g., 15000000"
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700">
                  Create Target
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="bg-gray-200 px-6 py-2 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetTargetSetting;